import type { Slot } from "./client.ts";

function stable(value: unknown): string {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return JSON.stringify(Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b)).map(([key, item]) => [key, stable(item)])));
  }
  return JSON.stringify(value);
}
function position(slot: Slot): string {
  return stable([slot.id, slot.player_id, slot.kind, slot.host.id, slot.recipient?.id,
    slot.white_id, slot.black_id, slot.snapshot_digest, stable(slot.terms)]);
}

/** A foreground click accepts this pair and position, including one host approval.
 * Never persisted: reload, hiding the tab or losing the connection needs a new click.
 */
export class LiveOfferConsent {
  private readonly position: string;
  private revision: number;
  private approved: boolean;
  constructor(slot: Slot, private readonly createdAt = Date.now()) {
    this.position = position(slot); this.revision = slot.revision; this.approved = slot.approved;
  }
  accepts(slot: Slot, now = Date.now()): boolean {
    if (now - this.createdAt > 120_000 || now < this.createdAt || slot.kind !== "import" || slot.state !== "paired"
      || !slot.recipient || !slot.white_id || !slot.black_id || position(slot) !== this.position) return false;
    if (slot.revision === this.revision && slot.approved === this.approved) return true;
    if (!this.approved && slot.approved && slot.revision === this.revision + 1) {
      this.revision = slot.revision; this.approved = true; return true;
    }
    return false;
  }
}

// A single, one-use handoff between the invitation and slot routes in this tab.
let accepted: { slot: string; consent: LiveOfferConsent } | undefined;
function clearAccepted(): void { accepted = undefined; document.removeEventListener("visibilitychange", hidden); }
function hidden(): void { if (document.visibilityState !== "visible") clearAccepted(); }
export function acceptLiveOffer(slot: Slot): void {
  clearAccepted();
  if (document.visibilityState !== "visible") return;
  accepted = { slot: slot.id, consent: new LiveOfferConsent(slot) };
  document.addEventListener("visibilitychange", hidden);
}
export function takeLiveOffer(slot: Slot): LiveOfferConsent | null {
  const value = accepted; clearAccepted();
  return document.visibilityState === "visible" && value?.slot === slot.id && value.consent.accepts(slot) ? value.consent : null;
}
